import { useState, useCallback, useEffect } from 'react'
import {Strong} from '@/components/shared/Strong'
import {MonoText} from '@/components/shared/MonoText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {FormGrid} from '@/components/shared/FormGrid'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {Sparkles, ChevronDown, ChevronUp} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { DetailsCard } from '@/components/shared/DetailsCard'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import { useToast } from '@/hooks/use-toast'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import { useI18n } from '@/i18n'
import { ApiError } from '@/api/client'
import { routes } from '@/lib/routes'
import {PageLayout} from '@/components/shared/PageLayout'
import { MutedText } from '@/components/shared/MutedText'
import {CardSection} from '@/components/shared/CardSection'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {Icon} from '@/components/shared/Icon'
import { formatTimestamp } from '@/lib/date-utils'
import type { LlmPromptTemplateUpdateRequest } from '@/types/llm-prompts'
import {useServices} from '@/services/context'
import {CONCEPT_ICON} from '@/lib/concepts'

export function LlmPromptDetail() {
    const services = useServices()
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const i18n = useI18n()
  const [editorContent, setEditorContent] = useState<string>('')
  const [isDirty, setIsDirty] = useState(false)
  const restoreDialog = useDialog()
  const [testPanelOpen, setTestPanelOpen] = useState(false)
  const [sampleInput, setSampleInput] = useState('')
  const [renderedPreview, setRenderedPreview] = useState<string | null>(null)
  const [llmResponse, setLlmResponse] = useState<string | null>(null)

  const { data: template, isLoading, error } = useQuery({
    queryKey: ['llm-prompts', key],
    queryFn: () => services.llmPrompts.detail(key!),
    enabled: !!key,
  })

  useEffect(() => {
    if (template) {
      setEditorContent(template.systemPrompt)
      setIsDirty(false)
    }
  }, [template])

  const handleEditorChange = useCallback((value: string) => {
    setEditorContent(value)
    setIsDirty(value !== template?.systemPrompt)
  }, [template])

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const updateMutation = useEntityMutation({
    mutationFn: (data: LlmPromptTemplateUpdateRequest) =>
      services.llmPrompts.update(key!, data),
    invalidate: ['llm-prompts'],
    successMessage: i18n.llmPrompts.messages.saveSuccess,
    onSuccess: () => {
      setIsDirty(false)
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast({ description: i18n.llmPrompts.messages.versionConflict, variant: 'destructive' })
        queryClient.invalidateQueries({ queryKey: ['llm-prompts', key] })
      }
    },
  })

  const restoreMutation = useEntityMutation({
    mutationFn: () =>
      services.llmPrompts.restoreDefault(key!),
    invalidate: ['llm-prompts'],
    successMessage: i18n.llmPrompts.messages.restoreSuccess,
    closeDialog: restoreDialog.close,
    onSuccess: () => {
      setIsDirty(false)
    },
  })

  const previewMutation = useMutation({
    mutationFn: () =>
      services.llmPrompts.renderPreview(key!, { variables: {} }),
    onSuccess: (data) => {
      setRenderedPreview(data.renderedPrompt)
    },
  })

  const testMutation = useMutation({
    mutationFn: () =>
      services.llmPrompts.test(key!, { sampleInput }),
    onSuccess: (data) => {
      setRenderedPreview(data.renderedPrompt)
      setLlmResponse(data.llmResponse)
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 503) {
        toast({ description: i18n.llmPrompts.messages.llmNotConfigured, variant: 'destructive' })
      }
    },
  })

  const handleSave = () => {
    if (!template) return
    updateMutation.mutate({
      systemPrompt: editorContent,
      version: template.version,
    })
  }

  const handleCancel = () => {
    navigate(routes.admin.llmPrompts.list())
  }

  return (
    <PageLayout
      variant="detail"
      title={template?.displayName ?? i18n.llmPrompts.detail.title}
      description={template?.key}
      icon={CONCEPT_ICON.llmPrompt}
      loading={isLoading || !template}
      error={error}
      backTo={routes.admin.llmPrompts.list()}
      data-testid="llm-prompt-detail-page"
    >
      {template && <>
      <div>
      {/* Metadata Card */}
      <DetailsCard
        data-testid="card-llm-prompt-metadata"
        title={i18n.llmPrompts.detail.metadata}
        fields={[
          {label: i18n.llmPrompts.columns.key, value: <MonoText>{template.key}</MonoText>},
          {label: i18n.llmPrompts.columns.description, value: template.description},
          {label: i18n.llmPrompts.columns.lastUpdated, value: formatTimestamp(template.updatedAt)},
        ]}
      />

      {/* Prompt Editor Card */}
      <Card>
        <CardHeader>
          <CardTitle data-testid="card-title-prompt-editor">{i18n.llmPrompts.detail.promptEditor}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div data-testid="llm-prompt-editor">
            <Textarea
              value={editorContent}
              onChange={(e) => handleEditorChange(e.target.value)}
              className="font-mono text-sm resize-y min-h-[300px] max-h-[600px]"
              data-testid="llm-prompt-textarea"
            />
          </div>

          {/* Variables reference panel */}
          <CardSection title={i18n.llmPrompts.detail.variables} data-testid="llm-prompt-variables">
            {template.variables.length === 0 ? (
              <MutedText size="sm">{i18n.llmPrompts.detail.noVariables}</MutedText>
            ) : (
              <Stack gap="sm">
                <MutedText size="sm">{i18n.llmPrompts.detail.variableDescription}</MutedText>
                <FormGrid>
                  {template.variables.map((v) => (
                    <InlineRow key={v.name} align="start" gap="md">
                      <Badge variant="secondary" className="font-mono shrink-0">{`{{${v.name}}}`}</Badge>
                      <MutedText size="sm">{v.description}</MutedText>
                    </InlineRow>
                  ))}
                </FormGrid>
              </Stack>
            )}
          </CardSection>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            variant="destructive"
            onClick={restoreDialog.open}
            data-testid="btn-restore-default"
          >
            {i18n.llmPrompts.actions.restoreDefault}
          </Button>
          <InlineRow>
            <Button
              variant="outline"
              onClick={handleCancel}
              data-testid="btn-cancel-prompt"
            >
              {i18n.llmPrompts.actions.cancel}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
              data-testid="btn-save-prompt"
            >
              {updateMutation.isPending ? i18n.common.saving : i18n.llmPrompts.actions.save}
            </Button>
          </InlineRow>
        </CardFooter>
      </Card>

      {/* Test/Preview Panel */}
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => setTestPanelOpen(!testPanelOpen)}
          data-testid="btn-toggle-test-panel"
        >
          <InlineRow gap="md">
            <Icon icon={Sparkles}/>
            <CardTitle data-testid="card-title-test-panel" className="text-base">{i18n.llmPrompts.testPanel.title}</CardTitle>
            <Icon icon={testPanelOpen ? ChevronUp : ChevronDown} className="ml-auto"/>
          </InlineRow>
        </CardHeader>
        {testPanelOpen && (
          <CardContent className="space-y-4">
            <div>
              <Strong>{i18n.llmPrompts.testPanel.sampleInput}</Strong>
              <Textarea
                value={sampleInput}
                onChange={(e) => setSampleInput(e.target.value)}
                placeholder={i18n.llmPrompts.testPanel.sampleInputPlaceholder}
                className="mt-1"
                data-testid="input-test-request"
              />
            </div>
            <InlineRow>
              <Button
                variant="outline"
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending}
                data-testid="btn-render-preview"
              >
                {i18n.llmPrompts.actions.renderPreview}
              </Button>
              <Button
                variant="secondary"
                onClick={() => testMutation.mutate()}
                disabled={!sampleInput.trim() || testMutation.isPending}
                data-testid="btn-send-to-llm"
              >
                {i18n.llmPrompts.actions.sendToLlm}
              </Button>
            </InlineRow>
            {renderedPreview && (
              <Stack gap="xs">
                <Strong>{i18n.llmPrompts.testPanel.renderedPrompt}</Strong>
                <CodeBlock maxHeight="lg" data-testid="rendered-prompt-preview">
                  {renderedPreview}
                </CodeBlock>
              </Stack>
            )}
            {llmResponse && (
              <Stack gap="xs">
                <Strong>{i18n.llmPrompts.testPanel.llmResponse}</Strong>
                <CodeBlock maxHeight="lg" data-testid="llm-test-response">
                  {llmResponse}
                </CodeBlock>
              </Stack>
            )}
          </CardContent>
        )}
      </Card>
      </div>

      {/* Restore Default Dialog */}
      <Dialog open={restoreDialog.isOpen} onOpenChange={restoreDialog.bind.onOpenChange}>
        <DialogContent data-testid="dialog-restore-default">
          <DialogHeader>
            <DialogTitle>{i18n.llmPrompts.messages.restoreConfirm}</DialogTitle>
            <DialogDescription>
              {i18n.llmPrompts.messages.restoreConfirmDescription}
            </DialogDescription>
          </DialogHeader>
          <SaveCancelFooter
            testIdPrefix="restore"
            onCancel={restoreDialog.close}
            saving={restoreMutation.isPending}
            saveLabel={i18n.llmPrompts.actions.restoreDefault}
            destructive
            saveType="button"
            onSave={() => restoreMutation.mutate()}
          />
        </DialogContent>
      </Dialog>

    </>}
    </PageLayout>
  )
}
